import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Globe, Award, GraduationCap, Monitor, 
  Cpu, CheckCircle, Briefcase, Target, Clock, FileText,
  MapPin, TrendingUp, Layers, Download, MessageSquare, Sparkles
} from 'lucide-react';

const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const trustFeatures = [
    { icon: Monitor, label: '100% Online Learning', desc: 'Flexible study from anywhere.' },
    { icon: Award, label: 'Scholarship-Supported Access', desc: 'Tuition support for eligible Afro-Asia learners.' },
    { icon: GraduationCap, label: 'Professional Programs', desc: 'Humanitarian, development, project management, and doctoral-level pathways.' },
    { icon: Target, label: 'Practical Skills', desc: 'Programs designed around real humanitarian and development work.' },
  ];

  const programs = [
    {
      title: 'Master of Humanitarian Action & Development',
      duration: '1 Year',
      mode: '100% Online',
      fullFee: 'USD 2,000',
      scholarshipFee: 'USD 200',
      structure: '9 Courses + Capstone Field Project',
      description: 'A professionally oriented program covering humanitarian principles, international law, NGO program management, MEAL, emergency response, donor systems, protection, logistics, and humanitarian leadership.',
      link: '/program/masters'
    },
    {
      title: 'Master of Development Project & Program Management',
      duration: '9 Months',
      mode: '100% Online',
      fullFee: 'USD 3,500',
      scholarshipFee: 'USD 350',
      structure: '5 Courses + Capstone Field Project',
      description: 'A practical development-sector program focused on project management, program management, MEAL, project finance, Theory of Change, donor requirements, and applied development practice.',
      link: '/program/masters-ppm'
    },
    {
      title: 'Doctor of Humanitarian Action & Development',
      duration: '3 Years',
      mode: '100% Online',
      fullFee: 'USD 10,800',
      scholarshipFee: 'USD 1,080',
      structure: '8 Courses + Doctoral Dissertation',
      description: 'An advanced doctoral-level pathway for experienced professionals seeking to strengthen research, policy, leadership, ethics, and knowledge contribution in humanitarian and development contexts.',
      link: '/program/doctoral'
    }
  ];

  const scholarshipTable = [
    { program: 'Master of Humanitarian Action & Development', fullFee: 'USD 2,000', scholarshipFee: 'USD 200' },
    { program: 'Master of Development Project & Program Management', fullFee: 'USD 3,500', scholarshipFee: 'USD 350' },
    { program: 'Doctor of Humanitarian Action & Development', fullFee: 'USD 10,800', scholarshipFee: 'USD 1,080' }
  ];

  const whyChooseFeatures = [
    { icon: Globe, title: 'Accessible Learning', desc: 'Online programs designed for learners across diverse geographic and professional contexts.' },
    { icon: MapPin, title: 'Afro-Asia Focus', desc: 'Scholarship-supported access for eligible learners from Africa and Asia.' },
    { icon: Target, title: 'Practical Relevance', desc: 'Curricula connected to humanitarian, development, NGO, donor-funded, and social impact work.' },
    { icon: Clock, title: 'Flexible Study', desc: 'Online learning designed for working professionals, field staff, and learners with competing responsibilities.' },
    { icon: TrendingUp, title: 'Professional Growth', desc: 'Programs support development of knowledge, confidence, leadership, and applied professional capacity.' },
    { icon: Layers, title: 'Global Perspective', desc: 'Learning informed by international humanitarian and development debates, standards, and field realities.' }
  ];

  const learningAreas = [
    'Humanitarian Action', 'Sustainable Development', 'Project Management', 'Program Management',
    'MEAL', 'Development Finance', 'Donor Systems', 'Proposal Development',
    'Protection, Gender & Inclusion', 'Emergency Response', 'Logistics & Operations',
    'Leadership & Coordination', 'Research Methods', 'Policy Analysis', 'Global Health', 'Responsible AI Use'
  ];

  const countries = [
    { name: 'Afghanistan',    flag: '\u{1F1E6}\u{1F1EB}' },
    { name: 'Benin',          flag: '\u{1F1E7}\u{1F1EF}' },
    { name: "Côte d\u2019Ivoire", flag: '\u{1F1E8}\u{1F1EE}' },
    { name: 'Ethiopia',       flag: '\u{1F1EA}\u{1F1F9}' },
    { name: 'Ghana',          flag: '\u{1F1EC}\u{1F1ED}' },
    { name: 'Kenya',          flag: '\u{1F1F0}\u{1F1EA}' },
    { name: 'Malawi',         flag: '\u{1F1F2}\u{1F1FC}' },
    { name: 'Myanmar',        flag: '\u{1F1F2}\u{1F1F2}' },
    { name: 'Nepal',          flag: '\u{1F1F3}\u{1F1F5}' },
    { name: 'Nigeria',        flag: '\u{1F1F3}\u{1F1EC}' },
    { name: 'Philippines',    flag: '\u{1F1F5}\u{1F1ED}' },
    { name: 'Sierra Leone',   flag: '\u{1F1F8}\u{1F1F1}' },
    { name: 'Somalia',        flag: '\u{1F1F8}\u{1F1F4}' },
    { name: 'South Sudan',    flag: '\u{1F1F8}\u{1F1F8}' },
    { name: 'Sudan',          flag: '\u{1F1F8}\u{1F1E9}' },
    { name: 'Syria',          flag: '\u{1F1F8}\u{1F1FE}' },
    { name: 'Togo',           flag: '\u{1F1F9}\u{1F1EC}' },
    { name: 'Uganda',         flag: '\u{1F1FA}\u{1F1EC}' },
    { name: 'Zambia',         flag: '\u{1F1FF}\u{1F1F2}' },
    { name: 'Zimbabwe',       flag: '\u{1F1FF}\u{1F1FC}' },
  ];

  const admissionSteps = [
    { step: 1, title: 'Choose Your Program', desc: 'Review available programs or short courses.' },
    { step: 2, title: 'Submit Your Application', desc: 'Complete the online application or enrollment form.' },
    { step: 3, title: 'Application Review', desc: 'The Admissions Team reviews your eligibility and submitted information.' },
    { step: 4, title: 'Admission & Tuition Confirmation', desc: 'Successful applicants receive admission and payment guidance.' },
    { step: 5, title: 'Begin Learning Online', desc: 'Confirmed students receive access instructions and begin their learning journey.' }
  ];

  const studentSupport = [
    { icon: Monitor, title: 'Online Learning Access', desc: 'Structured materials and video-based lessons.' },
    { icon: Download, title: 'Downloadable PDFs', desc: 'Resources available for offline study.' },
    { icon: FileText, title: 'Practical Assignments', desc: 'Applied exercises for real-world skills.' },
    { icon: MessageSquare, title: 'Academic Review', desc: 'Feedback and guidance from faculty.' }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section - DARK background, WHITE text */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url('/images/hero-mountains.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#102A43]/75 via-[#102A43]/70 to-[#102A43]/85" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 sm:py-28 md:py-40">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C82032] mb-6"
            >
              Geneva Institute of Humanitarian & Development Studies
            </motion.p>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-['Source_Serif_4'] text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.15] mb-6"
            >
              Accessible Humanitarian and Development Education for Global Professionals
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-slate-300 leading-relaxed mb-4"
            >
              The Geneva Institute of Humanitarian & Development Studies provides flexible online professional education in humanitarian action, development practice, project and program management, leadership, research, and applied professional skills.
            </motion.p>

            <motion.p 
              variants={fadeInUp}
              className="text-base text-slate-400 mb-10"
            >
              Designed for learners across Africa, Asia, and beyond.
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/programs"
                className="inline-flex items-center justify-center gap-2 bg-[#163A59] hover:bg-[#102A43] text-white px-8 py-4 text-sm font-semibold tracking-wide transition-all"
                data-testid="hero-programs-btn"
              >
                Explore Programs
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/scholarship"
                className="inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-8 py-4 text-sm font-semibold tracking-wide transition-all"
                data-testid="hero-scholarship-btn"
              >
                Apply for Scholarship
              </Link>
              <Link
                to="/certificate/ai-humanitarian"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-4 text-sm font-semibold tracking-wide transition-all backdrop-blur-sm"
                data-testid="hero-ai-course-btn"
              >
                View AI Short Course
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quick Trust Strip - WHITE background, DARK text */}
      <section className="py-16 md:py-20 bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustFeatures.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-4 sm:p-6"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#163A59]/10 text-[#163A59] rounded-sm mb-4">
                  <item.icon size={26} strokeWidth={1.5} />
                </div>
                <h3 className="font-['Source_Serif_4'] font-semibold text-lg text-slate-900 mb-2">{item.label}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach - showcase 20 active-student countries */}
      <section className="py-20 md:py-28 bg-white border-y border-[#DCE4EA]" data-testid="global-reach-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-16 items-start">
            {/* Left: heading + stat */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C82032] mb-4">
                Global Reach
              </p>
              <div className="flex items-baseline gap-3 mb-4">
                <span className="font-['Source_Serif_4'] text-6xl md:text-7xl font-bold text-[#163A59] leading-none">20</span>
                <span className="text-lg font-semibold text-[#52667A]">countries</span>
              </div>
              <h2 className="font-['Source_Serif_4'] text-2xl md:text-3xl font-bold text-[#102A43] mb-4">
                Active learners across Africa and Asia
              </h2>
              <p className="text-[#52667A] leading-relaxed mb-6">
                Our online programs support committed professionals and practitioners from 20 countries — building a growing network of humanitarian and development leaders across the Global South.
              </p>
              <div className="inline-flex items-center gap-2 text-sm text-[#163A59] font-semibold">
                <MapPin size={16} className="text-[#C82032]" />
                Africa · Asia
              </div>
            </motion.div>

            {/* Right: country grid with flags */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
            >
              {countries.map((c, i) => (
                <motion.div
                  key={c.name}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.03 }}
                  viewport={{ once: true }}
                  className="group flex items-center gap-3 bg-[#F1F5F8] hover:bg-white border border-[#DCE4EA] hover:border-[#163A59] hover:shadow-md px-3 py-2.5 transition-all duration-200"
                >
                  <span className="text-2xl leading-none" aria-hidden="true">{c.flag}</span>
                  <span className="text-sm font-medium text-[#102A43] group-hover:text-[#163A59] transition-colors">{c.name}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Snapshot - LIGHT GRAY background, DARK text */}
      <section className="py-20 md:py-28 bg-[#F1F5F8]" data-testid="about-snapshot-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
                About the Institute
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-8">
                Who We Are
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                The Geneva Institute of Humanitarian & Development Studies is an independent online professional education institute dedicated to advancing accessible, practice-informed, and globally relevant learning in humanitarian action, development, project and program management, leadership, research, policy, and applied professional practice.
              </p>
              <p className="text-slate-600 leading-relaxed mb-10">
                GIHDS supports learners seeking to strengthen their knowledge, professional judgment, and practical capacity to contribute effectively in humanitarian, development, NGO, public sector, and social impact settings.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[#163A59] font-semibold text-sm hover:gap-3 transition-all"
                data-testid="home-about-link"
              >
                Learn More About GIHDS
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Programs - WHITE background, DARK text */}
      <section className="py-20 md:py-28 bg-white" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              Our Programs
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              Explore Our Programs
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Structured online programs for learners seeking professional growth in humanitarian action, development practice, project and program management, leadership, and applied research.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#F1F5F8] border border-[#DCE4EA] hover:border-[#163A59]/30 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="font-['Source_Serif_4'] text-xl font-semibold text-slate-900 mb-5">
                    {program.title}
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-white p-3 border border-[#DCE4EA]">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">Duration</p>
                      <p className="font-semibold text-slate-900 text-sm">{program.duration}</p>
                    </div>
                    <div className="bg-white p-3 border border-[#DCE4EA]">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">Mode</p>
                      <p className="font-semibold text-slate-900 text-sm">{program.mode}</p>
                    </div>
                    <div className="bg-white p-3 border border-[#DCE4EA]">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">Full Fee</p>
                      <p className="font-semibold text-slate-900 text-sm">{program.fullFee}</p>
                    </div>
                    <div className="bg-white p-3 border border-[#DCE4EA]">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">Structure</p>
                      <p className="font-semibold text-slate-900 text-[0.8rem]">{program.structure}</p>
                    </div>
                  </div>

                  <p className="text-[0.7rem] text-[#C82032] font-medium mb-5" data-testid="scholarship-note">
                    * 90% scholarship available for eligible Afro-Asia students
                  </p>

                  <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-1">
                    {program.description}
                  </p>

                  <Link
                    to={program.link}
                    className="inline-flex items-center gap-2 text-[#163A59] font-semibold text-sm hover:gap-3 transition-all"
                  >
                    View Program
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Scholarship Section - LIGHT background */}
      <section className="relative py-20 md:py-28 bg-[#FAFBFC]" data-testid="scholarship-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C82032] mb-4">
                Scholarship Opportunity
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-[#102A43] mb-6">
                GIHDS Global Scholarship Initiative
              </h2>
              <p className="text-base text-[#52667A] mb-4">
                Expanding access to humanitarian and development education for qualified learners from Africa, Asia, and resource-constrained contexts.
              </p>
              <p className="text-base text-[#52667A] leading-relaxed mb-8">
                Through the GIHDS Global Scholarship Initiative, eligible applicants may receive substantial institutional tuition support for selected programs. The initiative is designed to reduce financial barriers and support committed learners seeking to strengthen their professional capacity in humanitarian action, development practice, project and program management, leadership, research, and social impact.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/scholarship"
                  className="inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-8 py-4 text-sm font-semibold tracking-wide transition-all"
                  data-testid="home-scholarship-btn"
                >
                  View Scholarship Initiative
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/admissions"
                  className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#163A59] text-[#163A59] px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#163A59] hover:text-white transition-all"
                  data-testid="home-apply-btn"
                >
                  Apply Now
                </Link>
              </div>
            </div>

            <div className="bg-[#163A59] shadow-lg overflow-hidden">
              <div className="p-6">
                <h3 className="font-semibold text-white text-lg mb-4">Program Tuition</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="text-left text-white/80 font-semibold text-xs uppercase tracking-wider py-3 pr-4">Program</th>
                        <th className="text-right text-white/80 font-semibold text-xs uppercase tracking-wider py-3 pl-2">Full Fee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scholarshipTable.map((row, index) => (
                        <tr key={row.program} className={index !== scholarshipTable.length - 1 ? 'border-b border-white/10' : ''}>
                          <td className="text-white py-3 pr-4 text-xs">{row.program}</td>
                          <td className="text-white font-semibold text-right py-3 pl-2">{row.fullFee}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-5 pt-4 border-t border-white/15 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-[#FCA5A5] mt-0.5 flex-shrink-0" />
                  <p className="text-white/85 text-xs leading-relaxed">
                    <span className="font-semibold text-[#FCA5A5]">90% Scholarship</span> available for eligible students from Africa and Asia. See scholarship page for eligibility.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Course Section - LIGHT background, contained BLUE card on right */}
      <section className="py-20 md:py-28 bg-[#F1F5F8]" data-testid="ai-course-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-[#C82032] text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider mb-4">
                <Cpu size={14} />
                New Professional Course
              </div>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-[#102A43] mb-4">
                AI in Humanitarian Action & Development
              </h2>
              <p className="text-[#52667A] leading-relaxed mb-4">
                Develop practical skills to use AI tools across the full humanitarian and development program cycle—from needs assessment and project design to donor mapping, implementation, MEAL, reporting, and professional productivity.
              </p>
              <p className="text-[#52667A] text-sm mb-8">
                Includes a Humanitarian & Development AI Prompt Bank with more than 200 reusable prompts.
              </p>
              <Link
                to="/certificate/ai-humanitarian"
                className="inline-flex items-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-8 py-4 text-sm font-semibold tracking-wide transition-all"
                data-testid="home-ai-course-btn"
              >
                Explore the Course
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-[#163A59] p-8 shadow-lg"
            >
              <h3 className="text-white font-semibold text-lg mb-6">Course Highlights</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/[0.06] border border-white/10 p-4">
                  <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Duration</p>
                  <p className="text-white font-semibold">4 Weeks</p>
                </div>
                <div className="bg-white/[0.06] border border-white/10 p-4">
                  <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Mode</p>
                  <p className="text-white font-semibold">100% Online</p>
                </div>
                <div className="bg-white/[0.06] border border-white/10 p-4">
                  <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Standard Fee</p>
                  <p className="text-[#FCA5A5] font-bold text-xl">USD 99</p>
                </div>
                <div className="bg-white/[0.06] border border-white/10 p-4">
                  <p className="text-white/70 text-xs uppercase tracking-wider mb-1">GIHDS Students</p>
                  <p className="text-white font-semibold">USD 59</p>
                </div>
              </div>
              <div className="mt-6 space-y-2">
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#FCA5A5]" />
                  <span>200+ Practical AI Prompts</span>
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#FCA5A5]" />
                  <span>Self-Paced Learning</span>
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#FCA5A5]" />
                  <span>Certificate of Completion</span>
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#FCA5A5]" />
                  <span>No Technical Experience Required</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose GIHDS - WHITE background, DARK text */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              Our Advantages
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900">
              Why Choose GIHDS
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#F1F5F8] p-8 hover:bg-slate-100 transition-colors"
              >
                <feature.icon className="w-10 h-10 text-[#163A59] mb-4" strokeWidth={1.5} />
                <h3 className="font-['Source_Serif_4'] font-semibold text-lg text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Areas - LIGHT GRAY background, DARK text */}
      <section className="py-20 md:py-28 bg-[#F1F5F8]" data-testid="learning-areas-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              Areas of Focus
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900">
              Our Learning Areas
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {learningAreas.map((area, index) => (
              <motion.span
                key={area}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
                viewport={{ once: true }}
                className="bg-white border border-[#DCE4EA] px-4 py-2 text-slate-700 text-sm hover:border-[#163A59] hover:text-[#163A59] transition-colors cursor-default"
              >
                {area}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Admissions Process - WHITE background, DARK text */}
      <section className="py-20 md:py-28 bg-white" data-testid="admissions-process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
                Application Process
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                How to Apply
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our streamlined application process is designed to be straightforward and efficient. Follow these steps to begin your learning journey with GIHDS.
              </p>
              <Link
                to="/admissions"
                className="inline-flex items-center gap-2 bg-[#163A59] hover:bg-[#102A43] text-white px-8 py-4 text-sm font-semibold tracking-wide transition-all"
                data-testid="home-start-application-btn"
              >
                Start Application
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="space-y-4">
              {admissionSteps.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4"
                >
                  <div className="w-10 h-10 bg-[#163A59] text-white flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div className="border-b border-[#DCE4EA] pb-4 flex-1">
                    <h4 className="font-semibold text-slate-900">{item.title}</h4>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Student Support - BLUE background, WHITE text */}
      <section className="py-20 md:py-28 bg-[#163A59]" data-testid="student-support-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80 mb-4">
              Learning Experience
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-white mb-6">
              Designed for Online Learners
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              GIHDS programs are designed to support flexible online learning. Students access structured materials, downloadable resources, video-based lessons, assignments, and academic guidance according to the requirements of each program.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {studentSupport.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm p-6 text-center border border-white/10"
              >
                <item.icon className="w-10 h-10 text-white mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-white/70 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Section - LIGHT GRAY background, DARK text */}
      <section className="py-20 md:py-28 bg-[#F1F5F8]" data-testid="career-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Briefcase className="w-12 h-12 text-[#163A59] mx-auto mb-6" strokeWidth={1.5} />
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
                Professional Pathways
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Prepare for Meaningful Professional Pathways
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                GIHDS programs are designed to support learners seeking roles or advancement in humanitarian, development, NGO, public sector, research, project management, MEAL, grants, operations, policy, and social impact settings.
              </p>
              <p className="text-slate-500 text-sm leading-relaxed">
                Career outcomes depend on each learner&apos;s prior education, experience, professional context, and labour market conditions. GIHDS does not guarantee employment, but its programs are designed to strengthen professional readiness and sector-relevant capacity.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA - DARK background, WHITE text */}
      <section className="py-20 md:py-28 bg-[#F1F5F8]" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <GraduationCap className="w-16 h-16 text-[#C82032] mx-auto mb-8" strokeWidth={1.5} />
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#102A43] mb-6">
              Start Your Learning Journey with GIHDS
            </h2>
            <p className="text-lg text-[#52667A] max-w-2xl mx-auto mb-10">
              Explore flexible online programs and professional short courses designed for humanitarian, development, NGO, public sector, and social impact professionals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/programs"
                className="inline-flex items-center justify-center gap-2 bg-[#163A59] hover:bg-[#102A43] text-white px-10 py-4 text-sm font-semibold tracking-wide transition-all"
                data-testid="home-final-programs-btn"
              >
                Explore Programs
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/scholarship"
                className="inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-10 py-4 text-sm font-semibold tracking-wide transition-all"
                data-testid="home-final-scholarship-btn"
              >
                Apply for Scholarship
              </Link>
              <Link
                to="/certificate/ai-humanitarian"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#163A59] text-[#163A59] px-10 py-4 text-sm font-semibold tracking-wide hover:bg-[#163A59] hover:text-white transition-all"
                data-testid="home-final-ai-btn"
              >
                Enroll in AI Short Course
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
